import { useRef, useState } from 'preact/hooks'
import { FieldLabel } from './FieldLabel'

/**
 * Segmented one-time-code field: one real `<input>` stretched invisibly over a row of cells that
 * render its value a character at a time.
 *
 * Six separate inputs would have to reassemble the code from six keystroke streams, and every
 * autofill path delivers it differently - iOS fills the focused field with the whole code, Chrome
 * fills the first one - so paste and SMS autofill are where that approach breaks. Keeping a single
 * ordinary field means the browser handles paste, autofill, password managers, IME and undo
 * exactly as it always does, and the cells are presentation only.
 *
 * @param {object} props
 * @param {string} props.value Digits entered so far; the parent owns sanitising and submission.
 * @param {(event: Event) => void} props.onInput
 * @param {number} [props.length]
 */
export const FieldCode = ({ value = '', onInput, label, placeholder, error, length = 6 }) => {
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef(null)

  /* Which cell the next character lands in, so the caret can be drawn on it. */
  const caretIndex = Math.min(value.length, length - 1)

  /**
   * Editing is append-and-backspace only: a caret dropped mid-string would type into a cell the
   * shopper did not aim at, since the cells are positional and the input is invisible.
   */
  const keepCaretAtEnd = (event) => {
    const input = event.currentTarget
    if (input.selectionStart !== input.value.length || input.selectionEnd !== input.value.length) {
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }

  return (
    <div className={'flex w-full flex-col gap-2'}>
      <FieldLabel {...{ label, hasInput: !!value }} />
      <div className={'relative flex flex-row gap-2'} data-role={'code-cells'}>
        {Array.from({ length }, (_, index) => (
          <div
            key={index}
            data-role={'code-cell'}
            data-ui={isFocused && index === caretIndex ? 'active' : ''}
            className={`paragraph-lg flex min-h-12 flex-1 items-center justify-center rounded-lg border border-solid text-center
            ${error ? 'border-system-error' : ''}
            ${isFocused && index === caretIndex ? 'border-2 border-secondary-green' : 'border-neutrals-tertiary-grey'}`}
          >
            {value[index] || ''}
          </div>
        ))}
        {/* Covers the cells, so a click anywhere in the row focuses the one real field. */}
        <input
          ref={inputRef}
          className={'absolute inset-0 h-full w-full cursor-default border-none bg-transparent text-center text-transparent caret-transparent outline-none focus-within:border-0 focus:border-0 focus-within:ring-0 focus:ring-0'}
          type={'text'}
          inputMode={'numeric'}
          autoComplete={'one-time-code'}
          maxLength={length}
          value={value}
          aria-label={label || placeholder}
          onInput={onInput}
          onChange={onInput}
          onFocus={(event) => {
            setIsFocused(true)
            keepCaretAtEnd(event)
          }}
          onBlur={() => setIsFocused(false)}
          onClick={keepCaretAtEnd}
          onSelect={keepCaretAtEnd}
        />
      </div>
      {error && <div className={'paragraph-sm font-medium text-system-error'}>{error}</div>}
    </div>
  )
}
